import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { AppComponentProps } from '@/typescriptHelpers';
import {
    IntegrationCapability,
    IntegrationJira,
    IntegrationType,
    SettingType,
    SubscriptionFeatureType,
    TriageTicketFilterGroup,
} from '@/types';
import { createAppSettingsRouteWithSettingType } from '@/helpers/routeHelpers';
import { selectAppIntegrations, selectAppSettings } from '@/selectors/selectors';
import { useUpdateTriageTicketFiltersMutation } from '@/middlewares/defaultApi';
import { globalMessage } from '@/utils/globalMessage';
import { updateAppSettingsSuccessAction } from '@/actions/appSettings';
import { useRestrictedFeatureIsDisabled } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import NewTicketFilterModal, { OrSeparator, triageFilterConditionLabel } from './NewTicketFilterModal';

// Renders the content of the "Ticket filters" row; the parent owns the Descriptions.Item.
const TicketFiltersSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const savedFilters = useSelector((state: RootState) => selectAppSettings(state, appId)?.triageTicketFilters);
    const savedGroups = React.useMemo(() => savedFilters?.conditionGroups ?? [], [savedFilters]);
    const jiraIntegration = useSelector((state: RootState) =>
        selectAppIntegrations(state, appId).find(
            (integration): integration is IntegrationJira => integration.id === IntegrationType.Jira,
        ),
    );
    const dispatch = useDispatch();
    const isPaywalled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const [updateTriageTicketFilters, { isLoading, data: updateResponse, originalArgs }] =
        useUpdateTriageTicketFiltersMutation();
    const [modalOpen, setModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (updateResponse?.success) {
            dispatch(updateAppSettingsSuccessAction({ appId, appSettings: updateResponse.data }));
            globalMessage.success({
                content:
                    originalArgs?.closeNonMatchingIssues ?
                        'Ticket filters updated! Issues no longer matching will be updated shortly.'
                    :   'Ticket filters updated!',
                key: 'triageTicketFilters',
            });
            setModalOpen(false);
        } else if (updateResponse?.errorMessage) {
            globalMessage.error(updateResponse.errorMessage);
        }
    }, [updateResponse, originalArgs, appId, dispatch]);

    const saveGroups = (conditionGroups: TriageTicketFilterGroup[], closeNonMatchingIssues: boolean) =>
        updateTriageTicketFilters({ orgId, appId, conditionGroups, closeNonMatchingIssues });

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div
                style={{ flex: 1, marginRight: 48, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
            >
                {savedGroups.length === 0 ?
                    <Text type="secondary">Not set, every ticket is pulled</Text>
                :   savedGroups.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                            {groupIndex > 0 && <OrSeparator />}
                            <div>
                                {savedGroups.length > 1 && (
                                    <Text style={{ display: 'block', paddingBottom: 4 }} strong>
                                        Group {groupIndex + 1}
                                    </Text>
                                )}
                                {(group.conditions ?? []).map((condition, conditionIndex) => (
                                    <Text
                                        key={`${condition.field}-${condition.fieldName ?? ''}`}
                                        style={{ display: 'block' }}
                                    >
                                        {conditionIndex > 0 && <Text type="secondary">and </Text>}
                                        {triageFilterConditionLabel(condition)}:{' '}
                                        {(condition.values ?? []).map((value, valueIndex) => (
                                            <React.Fragment key={value}>
                                                {valueIndex > 0 && ', '}
                                                {value.includes('{') ?
                                                    <Text code>{value}</Text>
                                                :   value}
                                            </React.Fragment>
                                        ))}
                                    </Text>
                                ))}
                            </div>
                        </React.Fragment>
                    ))
                }
            </div>
            <PermissionedActionButton
                actionType={RunwayUserAction.UAAddRemoveEditFeatureAffiliations}
                type="link"
                icon={<EditOutlined />}
                disabled={isPaywalled}
                onClick={() => setModalOpen(true)}
            >
                Edit
            </PermissionedActionButton>

            <NewTicketFilterModal
                open={modalOpen}
                isLoading={isLoading}
                groups={savedGroups}
                additionalFieldNames={jiraIntegration ? (jiraIntegration.data?.customFields ?? []) : undefined}
                additionalFieldsSettingsLink={{
                    pathname: createAppSettingsRouteWithSettingType(orgId, appId, SettingType.Integrations),
                    state: { scrollTo: IntegrationCapability.FeatureManagement },
                }}
                onCancel={() => setModalOpen(false)}
                onOk={saveGroups}
            />
        </div>
    );
};

export default TicketFiltersSettings;
